import { Accordion, AccordionDetails, AccordionSummary, IconButton, Link, useTheme } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import GetAppIcon from '@material-ui/icons/GetApp';
import LanguageIcon from '@material-ui/icons/Language';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PhoneIcon from '@material-ui/icons/Phone';
import YouTubeIcon from '@material-ui/icons/YouTube';
import { CardX } from 'framework/components/cards/CardX';
import { BadgeChipX } from 'framework/components/chips/BadgeChipX';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useLazyEffect } from 'framework/hooks/useLazyEffect';
import { TableWithHeadersAndValues } from 'framework/table/TableWithHeadersAndValues';
import { createHeader } from 'framework/table/createHeader';
import {
	getCochlearCounselingStepModules_handle,
	getCochlearReferralMaterials_handle,
	ICochlearPartnerTrainingModuleDto,
	ICochlearReferralMaterialDto,
} from 'gen/ApiClient';
import Fg1 from 'libs/ui/layout/fg1';
import ColumnStack from 'libs/ui/layout/stack/column.stack';
import RowStack from 'libs/ui/layout/stack/row.stack';
import { useLocalization } from 'localization/useLocalization';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { IReloadTriggerProps } from 'shared/layout/IReloadTriggerProps';
import { MediaAssetsCarousel, MediaAssetsCarouselDialog } from './cochlear.components';

type CochlearCounselingGuideTabProps = IReloadTriggerProps & {
	reload: VoidFunction;
};

export const CochlearCounselingGuideTab = ({ reloadTrigger }: CochlearCounselingGuideTabProps) => {
	return (
		<RowStack
			dfrow
			w100
			style={{ padding: '16px', gap: '8px' }}>
			<ModulesStack reloadTrigger={reloadTrigger} />
			<ReferralMaterialStack reloadTrigger={reloadTrigger} />
		</RowStack>
	);
};

type ModulesStackProps = IReloadTriggerProps & {};

const ModulesStack = ({ reloadTrigger }: ModulesStackProps) => {
	const [data, reloadEvents] = useApiEffect(getCochlearCounselingStepModules_handle);
	var fOrderedData = _.orderBy(data ?? [], t => t.order);
	const { open, confirm } = useDialogsContext();

	useLazyEffect(() => {
		reloadEvents();
	}, [reloadTrigger]);

	const openDialog = (item: ICochlearPartnerTrainingModuleDto) => {
		open(
			<MediaAssetsCarouselDialog
				assets={item.mediaAssets}
				close={confirm}
			/>
		);
	};

	return (
		<ColumnStack
			dfcol
			style={{ gap: '8px' }}
			w50>
			<div className='df-col'>
				{fOrderedData.map(t => (
					<Accordion key={t.id}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<div className='df-row-ac gap-8'>
								<BadgeChipX
									value={t.order}
									color='primary'
								/>
								<div style={{ fontWeight: 600 }}>{t.title}</div>
							</div>
						</AccordionSummary>
						<AccordionDetails>
							<div className='df-col'>
								<div
									dangerouslySetInnerHTML={{
										__html: t.htmlContent ?? '',
									}}
								/>
								<MediaAssetsCarousel
									assets={t.mediaAssets}
									openDialog={() => openDialog(t)}
									variant='embedded'
								/>
							</div>
						</AccordionDetails>
					</Accordion>
				))}
			</div>
			<RowStack
				dfrowac
				style={{
					backgroundColor: '#F1BF48',
					borderRadius: '16px',
				}}>
				<img
					src={'https://hamspublicfiles.blob.core.windows.net/cochlear/cochlear_logo_white.jpeg'}
					alt='logo white'
					style={{ height: '100px', marginLeft: '16px' }}
				/>
				<Fg1 />
				<ColumnStack dfcol>
					<RowStack
						dfrowac
						gap={0.5}>
						<PhoneIcon style={{ fontSize: '16px' }} />
						<Link href={`tel:003215795577`}>{`+32 (0)1579 55 77`}</Link>
					</RowStack>
					<RowStack
						dfrowac
						gap={0.5}>
						<MailOutlineIcon style={{ fontSize: '16px' }} />
						<Link href={`mailto:customerservice.bnl@cochlear.com`}>{`customerservice.bnl@cochlear.com`}</Link>
					</RowStack>
				</ColumnStack>
				<Fg1 />
				<RowStack
					dfrowac
					style={{ marginRight: '8px' }}>
					<IconButton
						size='small'
						edge='end'>
						<YouTubeIcon />
					</IconButton>
					<IconButton size='small'>
						<LanguageIcon />
					</IconButton>
				</RowStack>
			</RowStack>
		</ColumnStack>
	);
};

type ReferralMaterialStackProps = IReloadTriggerProps & {};

const ReferralMaterialStack = ({ reloadTrigger }: ReferralMaterialStackProps) => {
	const theme = useTheme();
	const strings = useLocalization();
	const [data, reload] = useApiEffect(getCochlearReferralMaterials_handle);

	useLazyEffect(() => {
		reload();
	}, [reloadTrigger]);

	const handleMailClick = (data: ICochlearReferralMaterialDto) => {
		const subject = 'Brochures aanvragen';
		const body = `Beste,

Ik wens graag volgende brochures te ontvangen:

Brochures en aantallen:
	1x ${data.title}

Ontvangstadres:


Met vriendelijke groeten`;

		window.location.href = `mailto:marking.bnl@cochlear.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
	};

	const headers = useMemo(
		() => [
			createHeader<ICochlearReferralMaterialDto>(strings.type, item => item.type),
			createHeader<ICochlearReferralMaterialDto>(strings.title, item => item.title),
			createHeader<ICochlearReferralMaterialDto>(strings.language, item => item.language),
			createHeader<ICochlearReferralMaterialDto>(``, item => (
				<RowStack
					dfrowac
					gap={0.5}>
					<Fg1 />
					<IconButton
						size='small'
						edge='end'
						color='primary'
						onClick={() => window.open(item.downloadUrl, '_blank', 'noopener,noreferrer')}>
						<GetAppIcon />
					</IconButton>
					<IconButton
						size='small'
						edge='end'
						color='primary'
						onClick={() => handleMailClick(item)}>
						<MailOutlineIcon />
					</IconButton>
				</RowStack>
			)),
		],
		// eslint-disable-next-line
		[strings]
	);

	return (
		<CardX style={{ height: 'fit-content', width: '50%' }}>
			<TableWithHeadersAndValues<ICochlearReferralMaterialDto>
				padding={theme.spacing(1)}
				headers={headers}
				getKey={t => t.id}
				values={data ?? []}
			/>
		</CardX>
	);
};
